import { SelectOption } from '@factorialco/deprecated-design-system'

import DayPicker from 'modules/core/components/DayPicker'
import MonthPicker from 'modules/core/components/MonthPicker'
import SelectField from 'modules/core/components/SelectField'
import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { dateBackendToUI, dateToString, dateUIToBackend } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import enableDay from 'modules/contracts/lib/enableDay'
import Compensation from 'modules/contracts/store/models/Compensation'
import { NO_POLICY_ID } from 'modules/employees/pages/BulkFixContractsPage/types'

import { FormSection } from '../_FormSection'
import {
  setFirstPaymentAction,
  setRecurrenceAction,
  setRecurrenceCountAction,
  setStartsOnAction,
} from '../actions'
import { NewCompensationStep, StepComponent, StepValidator } from '../types'

const ScheduleStep: StepComponent = ({ data, onUpdate }) => {
  const {
    syncWithSupplements,
    allowedPayrollPolicies,
    policyId,
    recurrenceCount,
    startsOn,
    recurrence,
    firstPaymentOn,
  } = data

  /* ===== State helpers ===== */
  const recurrenceCountOptions = useLazyRef(loadRecurrenceCountOptions)
  const recurrenceOptions = useLazyRef(loadRecurrenceOptions)

  const uiRecurrenceCount = `${recurrenceCount}`
  const policyMeta = useUnsafeMemo(() => {
    return allowedPayrollPolicies.find((policy) => policy.id === policyId)
  }, [allowedPayrollPolicies, policyId])

  /* ===== Event handlers ===== */

  const handleRecurrenceCountChange = useUnsafeCallback(
    (newRecurrenceCount: string) => {
      onUpdate(setRecurrenceCountAction(Number.parseInt(newRecurrenceCount)))
    },
    [onUpdate]
  )

  const handleStartsOnChange = useUnsafeCallback(
    (newStartsOnDate: string) => {
      if (newStartsOnDate) {
        const date = dateUIToBackend(newStartsOnDate)
        if (date) {
          onUpdate(setStartsOnAction(date))
        }
      }
    },
    [onUpdate]
  )

  const handleRecurrenceChange = useUnsafeCallback(
    (newRecurrence: string) => {
      onUpdate(setRecurrenceAction(newRecurrence))
    },
    [onUpdate]
  )

  const handleFirstPaymentChange = useUnsafeCallback(
    (newFirstPayment?: string) => {
      if (newFirstPayment) {
        const date = dateUIToBackend(newFirstPayment)
        if (date) {
          onUpdate(setFirstPaymentAction(date))
        }
      }
    },
    [onUpdate]
  )

  // In practice firstPaymentOn can't really be undefined
  const uiStartingOnValue = startsOn ? dateBackendToUI(startsOn) : undefined
  const uiFirstPaymentValue = dateBackendToUI(firstPaymentOn ?? dateToString(new Date(), 'ui'))

  return (
    <FormSection>
      {syncWithSupplements && policyMeta && policyMeta.id != NO_POLICY_ID ? (
        <>
          <SelectField
            mandatory
            label={i18n.t('employees.bulk_edit.fix_conditions.steps.schedule.repeat_label')}
            options={recurrenceCountOptions.current}
            value={uiRecurrenceCount}
            onChange={handleRecurrenceCountChange}
          />
          <DayPicker
            enableDay={enableDayForPolicy(policyMeta.recurrence, policyMeta?.startsOn)}
            label={i18n.t('compensation.starting_on')}
            value={uiStartingOnValue}
            onChange={handleStartsOnChange}
            mandatory
          />
        </>
      ) : (
        <>
          <SelectField
            mandatory
            label={i18n.t('employees.bulk_edit.fix_conditions.steps.schedule.repeat_label')}
            options={recurrenceOptions.current}
            value={recurrence}
            onChange={handleRecurrenceChange}
          />
          <MonthPicker
            label={i18n.t('compensation.starting_on')}
            value={uiFirstPaymentValue}
            onChange={handleFirstPaymentChange}
            mandatory
          />
        </>
      )}
    </FormSection>
  )
}

/* ===== Utilities ===== */

const enableDayForPolicy = (
  payrollPolicyRecurrence: string,
  payrollPolicyStart: string
): ((day: Date | moment.Moment) => boolean) => {
  return (day) =>
    enableDay({
      day: day,
      recurrence: payrollPolicyRecurrence,
      starts_on: payrollPolicyStart,
    })
}

/* ===== Select Option ===== */

function loadRecurrenceCountOptions(): SelectOption[] {
  const recurrenceCountOption = (count: number) => ({
    label: i18n.tp(
      'contract_versions.forms.additional_compensation.modal.every_n_payroll_periods',
      { count }
    ),
    value: count,
  })

  return [...Array(12).keys()].map((i) => recurrenceCountOption(i + 1))
}

function loadRecurrenceOptions(): SelectOption[] {
  return Object.entries(Compensation.recurrences()).map(([value, label]) => ({
    value,
    label,
  }))
}

/* ===== Validators ===== */

const isCompletedStep: StepValidator = ({
  recurrence,
  recurrenceCount,
  firstPaymentOn,
  startsOn,
  syncWithSupplements,
}) => {
  return syncWithSupplements ? !!recurrenceCount && !!startsOn : !!recurrence && !!firstPaymentOn
}

/* ===== Exports ===== */

const stepDefinition: NewCompensationStep = () => ({
  Component: ScheduleStep,
  isCompleted: isCompletedStep,
  title: i18n.t('employees.bulk_edit.fix_conditions.steps.schedule.title'),
})

export default stepDefinition
